import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const LocationPartnerAgreement = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      color: theme.text,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderBottom: `1px solid ${theme.border}`,
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 10,
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: theme.accent,
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      margin: 0,
      color: theme.text,
    },
    content: {
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: 1.6,
    },
    lastUpdated: {
      color: theme.textMuted,
      fontSize: '14px',
      marginBottom: '24px',
    },
    version: {
      color: theme.accent,
      fontSize: '13px',
      fontWeight: '600',
      marginBottom: '24px',
    },
    text: {
      color: theme.textSecondary,
      fontSize: '14px',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '32px',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: `1px solid ${theme.border}`,
    },
    subTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: theme.text,
      marginTop: '20px',
      marginBottom: '12px',
    },
    bold: {
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
    },
    list: {
      color: theme.textSecondary,
      fontSize: '14px',
      paddingLeft: '24px',
      marginBottom: '16px',
    },
    highlight: {
      color: '#0ea5e9',
      fontSize: '14px',
      fontWeight: '600',
      padding: '12px 16px',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    warningBox: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
    },
    warningTitle: {
      color: '#ef4444',
      fontSize: '14px',
      fontWeight: '700',
      margin: 0,
      textAlign: 'center',
    },
    finalText: {
      color: theme.textMuted,
      fontSize: '13px',
      marginTop: '32px',
      paddingTop: '16px',
      borderTop: `1px solid ${theme.border}`,
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={styles.title}>Location Partner Agreement</h1>
      </div>

      <div style={styles.content}>
        <p style={styles.lastUpdated}>Last Updated: March 11, 2026</p>
        <p style={styles.version}>Agreement Version 1.0</p>

        <p style={styles.text}>
          This Location Partner Agreement ("Agreement") is entered into between you ("Partner", "Location", "you") and CardChase ("we", "us", "our"). By claiming a location on CardChase and signing this Agreement, you agree to the following terms.
        </p>

        <h2 style={styles.sectionTitle}>1. Partner Program Overview</h2>

        <p style={styles.text}>
          The CardChase Location Partner Program allows verified business owners and authorized representatives to claim and manage their location's presence on the CardChase platform. As a Partner, you gain the ability to manage your location's profile, operating hours, events, and staff access.
        </p>

        <p style={styles.highlight}>
          Becoming a verified location does not constitute an endorsement by CardChase. It confirms that the business representative has been verified by our team.
        </p>

        <h2 style={styles.sectionTitle}>2. Eligibility and Verification</h2>

        <h3 style={styles.subTitle}>Who Can Claim a Location</h3>
        <ul style={styles.list}>
          <li>The business owner</li>
          <li>A general manager authorized to act on behalf of the business</li>
          <li>An authorized representative with explicit permission from the owner</li>
        </ul>

        <h3 style={styles.subTitle}>Verification Process</h3>
        <p style={styles.text}>
          After submitting a claim, our team will verify your identity and relationship to the business. This may include:
        </p>
        <ul style={styles.list}>
          <li>Calling the business phone number on file</li>
          <li>Verifying your email domain matches the business</li>
          <li>Requesting additional documentation if needed</li>
          <li>Confirming business details match public records</li>
        </ul>
        <p style={styles.text}>
          Verification typically takes 1-3 business days. CardChase reserves the right to deny any claim at our sole discretion.
        </p>

        <h2 style={styles.sectionTitle}>3. Partner Responsibilities</h2>

        <p style={styles.bold}>As a verified location partner, you agree to:</p>
        <ul style={styles.list}>
          <li>Provide accurate and current business information (address, phone, hours)</li>
          <li>Keep operating hours and event schedules up to date</li>
          <li>Maintain a safe and welcoming environment for CardChase users visiting your location</li>
          <li>Not misrepresent your business, services, or affiliation with CardChase</li>
          <li>Promptly update your profile if business details change</li>
          <li>Comply with all applicable local, state, and federal laws</li>
        </ul>

        <p style={styles.bold}>You agree NOT to:</p>
        <ul style={styles.list}>
          <li>Use CardChase to engage in fraudulent or deceptive practices</li>
          <li>Harass, discriminate against, or refuse service to CardChase users</li>
          <li>Share CardChase user data or use it for unauthorized marketing</li>
          <li>Misrepresent your relationship with CardChase (we are a listing platform, not a business partner or endorser)</li>
          <li>Claim a location you are not authorized to represent</li>
        </ul>

        <h2 style={styles.sectionTitle}>4. Data and Display</h2>

        <p style={styles.text}>
          By joining the Partner Program, you grant CardChase a non-exclusive, royalty-free license to display your business name, address, phone number, operating hours, photos, and other profile information you provide on the CardChase platform. This information may be visible to all CardChase users.
        </p>
        <p style={styles.text}>
          CardChase may display user-submitted ratings and reviews of your location. You may not attempt to manipulate, suppress, or artificially inflate these ratings.
        </p>

        <h2 style={styles.sectionTitle}>5. Subscription Tiers</h2>

        <p style={styles.text}>
          CardChase offers multiple subscription tiers with varying features. Your initial tier will be determined upon verification. Tier upgrades, features, and pricing are subject to change. CardChase will provide notice of any material changes to your subscription.
        </p>

        <h2 style={styles.sectionTitle}>6. Liability and Disclaimers</h2>

        <div style={styles.warningBox}>
          <p style={styles.warningTitle}>IMPORTANT LIABILITY INFORMATION</p>
        </div>

        <p style={styles.text}>
          CardChase is a platform that connects users. We are not a party to any transaction, trade, or interaction that occurs at your location.
        </p>

        <ul style={styles.list}>
          <li>CardChase is not liable for any incidents, injuries, theft, damages, or disputes that occur at your location</li>
          <li>You are solely responsible for the safety and security of your premises</li>
          <li>You agree to indemnify and hold CardChase harmless from any claims arising from events at your location</li>
          <li>Being listed as "verified" does not constitute a safety guarantee to users</li>
        </ul>

        <h2 style={styles.sectionTitle}>7. Ownership Disputes</h2>

        <p style={styles.text}>
          If another party disputes your claim to a location, CardChase will review the dispute and may request documentation from both parties. CardChase reserves the right to transfer, suspend, or revoke location ownership based on our review. Our decision is final.
        </p>

        <h2 style={styles.sectionTitle}>8. Termination</h2>

        <p style={styles.text}>
          Either party may terminate this Agreement at any time. CardChase may revoke your verified status or remove your location for:
        </p>
        <ul style={styles.list}>
          <li>Violation of this Agreement or CardChase Terms of Service</li>
          <li>Providing false or misleading information</li>
          <li>Fraudulent claim of ownership</li>
          <li>Complaints from users about safety or conduct</li>
          <li>Non-payment of subscription fees (if applicable)</li>
          <li>Any reason at CardChase's sole discretion</li>
        </ul>

        <h2 style={styles.sectionTitle}>9. Changes to This Agreement</h2>

        <p style={styles.text}>
          CardChase may update this Agreement. We will notify you via email or in-app notification of material changes. Continued use of the Partner Program after changes constitutes acceptance of the updated terms.
        </p>

        <h2 style={styles.sectionTitle}>10. Governing Law</h2>

        <p style={styles.text}>
          This Agreement is governed by the laws of the State of California. Any disputes arising from this Agreement will be resolved through binding arbitration in accordance with CardChase's Terms of Service.
        </p>

        <h2 style={styles.sectionTitle}>11. Contact Us</h2>
        <p style={styles.text}>
          <strong>CardChase Partner Support</strong><br />
          Email: tradersupport@cardchase.org<br />
          Address: Trainer Center, 4911 Warner Ave #210, Huntington Beach, CA 92649
        </p>

        <p style={styles.finalText}>
          By signing this Agreement, you acknowledge that you have read, understood, and agree to be bound by these terms as a CardChase Location Partner.
        </p>
      </div>
    </div>
  );
};

export default LocationPartnerAgreement;
